import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { 
  MapPin, 
  Sun,
  Moon,
  CheckCircle2,
  Users
} from "lucide-react";

const defaultUnits = [
  { name: "Unit 200", description: "Detox" },
  { name: "Unit 300", description: "Adult" },
  { name: "Unit 400", description: "Adult High Acuity" },
  { name: "Unit 500", description: "Adolescent" },
  { name: "Unit 600", description: "Adolescent" },
];

export default function Rounds() {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState(defaultUnits);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchRounds();
    fetchUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pull units from the user's primary facility. If they configured units
  // through the setup wizard or facility editor, those win. Otherwise we
  // fall back to the psychiatric defaults so legacy users see no change.
  const fetchUnits = async () => {
    try {
      const savedFacilityId = localStorage.getItem("active_facility_id");
      const res = await axios.get(`${API}/facilities`, { withCredentials: true });
      const list = res.data || [];
      const active = list.find(f => f.facility_id === savedFacilityId)
        || list.find(f => f.is_primary)
        || list[0];
      const configured = (active?.units || []).filter(u => u && u.name);
      if (configured.length > 0) {
        setUnits(configured);
      }
    } catch (_) { /* keep defaults on error */ }
  };

  const fetchRounds = async () => {
    try {
      const response = await axios.get(`${API}/rounds?date=${today}`, { withCredentials: true });
      setRounds(response.data || []);
    } catch (error) {
      console.error("Error fetching rounds:", error);
    } finally {
      setLoading(false);
    }
  };

  const createOrUpdateRound = async (unit, shift, data = {}) => {
    const existingRound = rounds.find(r => r.unit === unit && r.shift === shift);
    
    try {
      if (existingRound) {
        await axios.patch(`${API}/rounds/${existingRound.round_id}`, data, { withCredentials: true });
      } else {
        await axios.post(`${API}/rounds`, {
          unit,
          shift,
          ...data
        }, { withCredentials: true });
      }
      fetchRounds();
    } catch (error) {
      toast.error("Failed to update round");
    }
  };

  const toggleComplete = async (unit, shift) => {
    const existingRound = rounds.find(r => r.unit === unit && r.shift === shift);
    await createOrUpdateRound(unit, shift, { completed: !existingRound?.completed });
    toast.success(existingRound?.completed ? "Round unmarked" : "Round completed!");
  };

  const updateNote = async (unit, shift, field, value) => {
    await createOrUpdateRound(unit, shift, { [field]: value });
  };

  const getRound = (unit, shift) => rounds.find(r => r.unit === unit && r.shift === shift);

  const completedMorning = rounds.filter(r => r.shift === "morning" && r.completed).length;
  const completedAfternoon = rounds.filter(r => r.shift === "afternoon" && r.completed).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="rounds-page" className="space-y-6 animate-fade-in">
      {/* Header — stacks on phone, side-by-side from sm+ */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" />
            Rounds & Visibility
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Track unit visits and staff touchpoints</p>
        </div>
        
        <div className="flex gap-2 sm:gap-4 flex-wrap">
          <Badge variant="outline" className="gap-2">
            <Sun className="w-4 h-4 text-amber-400" />
            AM: {completedMorning}/{units.length}
          </Badge>
          <Badge variant="outline" className="gap-2">
            <Moon className="w-4 h-4 text-blue-400" />
            PM: {completedAfternoon}/{units.length}
          </Badge>
        </div>
      </div>

      {/* Units Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {units.map((unit) => {
          const morningRound = getRound(unit.name, "morning");
          const afternoonRound = getRound(unit.name, "afternoon");
          
          return (
            <Card key={unit.name} className="bento-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>{unit.name}</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    {unit.description}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Morning Round */}
                <div className="space-y-2 p-3 rounded-lg bg-zinc-800/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sun className="w-4 h-4 text-amber-400" />
                      <span className="text-sm font-medium">Morning</span>
                    </div>
                    <Checkbox
                      checked={morningRound?.completed || false}
                      onCheckedChange={() => toggleComplete(unit.name, "morning")}
                      data-testid={`morning-${unit.name}`}
                    />
                  </div>
                  
                  {morningRound?.completed && (
                    <div className="space-y-2 animate-fade-in">
                      <Input
                        placeholder="Biggest concern..."
                        value={morningRound?.biggest_concern || ""}
                        onChange={(e) => updateNote(unit.name, "morning", "biggest_concern", e.target.value)}
                        className="text-xs h-8"
                        data-testid={`morning-concern-${unit.name}`}
                      />
                      <Input
                        placeholder="Staff touchpoint..."
                        value={morningRound?.staff_touchpoint || ""}
                        onChange={(e) => updateNote(unit.name, "morning", "staff_touchpoint", e.target.value)}
                        className="text-xs h-8"
                        data-testid={`morning-staff-${unit.name}`}
                      />
                    </div>
                  )}
                </div>

                {/* Afternoon Round */}
                <div className="space-y-2 p-3 rounded-lg bg-zinc-800/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Moon className="w-4 h-4 text-blue-400" />
                      <span className="text-sm font-medium">Afternoon</span>
                    </div>
                    <Checkbox
                      checked={afternoonRound?.completed || false}
                      onCheckedChange={() => toggleComplete(unit.name, "afternoon")}
                      data-testid={`afternoon-${unit.name}`}
                    />
                  </div>
                  
                  {afternoonRound?.completed && (
                    <div className="space-y-2 animate-fade-in">
                      <Input
                        placeholder="Biggest concern..."
                        value={afternoonRound?.biggest_concern || ""}
                        onChange={(e) => updateNote(unit.name, "afternoon", "biggest_concern", e.target.value)}
                        className="text-xs h-8"
                        data-testid={`afternoon-concern-${unit.name}`}
                      />
                      <Input
                        placeholder="Staff touchpoint..."
                        value={afternoonRound?.staff_touchpoint || ""}
                        onChange={(e) => updateNote(unit.name, "afternoon", "staff_touchpoint", e.target.value)}
                        className="text-xs h-8"
                        data-testid={`afternoon-staff-${unit.name}`}
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Staff Interactions Summary */}
      <Card className="bento-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Today&apos;s Staff Touchpoints
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rounds
              .filter(r => r.staff_touchpoint)
              .map((round) => (
                <div 
                  key={round.round_id}
                  className="flex items-start gap-2 p-2 rounded-lg bg-zinc-800/30"
                >
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm">{round.staff_touchpoint}</p>
                    <p className="text-xs text-muted-foreground">
                      {round.unit} - {round.shift === "morning" ? "AM" : "PM"}
                    </p>
                  </div>
                </div>
              ))}
            {!rounds.some(r => r.staff_touchpoint) && (
              <p className="text-sm text-muted-foreground col-span-full text-center py-4">
                No staff touchpoints recorded yet
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
